import qs from 'qs';

import { fetchApi, getUserId } from 'shared/functions';
import { consolidatedPortfolioItemTypes } from 'shared/constants/portfolio/portfolioItemTypes';
import { ratingTypes } from 'shared/constants/ratingTypes';
import { serviceUrlPrefix } from 'utils';

export const fetchDossier = async ({ id, ratingType = ratingTypes.CREDIT_RATING }) => {
  const userId = getUserId();

  const urlPrefix = serviceUrlPrefix[ratingType];

  try {
    return await fetchApi(
      `${urlPrefix}/storefront/users/${userId}/dossiers/${id}/`,
    ).then(response => response.json());
  } catch (e) {
    throw new Error('Failed to fetch dossier: ', e);
  }
};

export const fetchDossierItemsSingleType = async ({
  dossierId,
  listId,
  itemType,
  ratingType = ratingTypes.CREDIT_RATING,
  page,
  limit,
  start,
  sort,
  filter,
}) => {
  const userId = getUserId();

  const params = {
    page: page || 1,
    start: start || 0,
    limit: limit || 25,
    ...(sort && { sort: sort.sortColumn }),
    ...(sort && { dir: sort.direction }),
    filter: filter ? JSON.stringify(filter) : undefined,
  };

  const urlPrefix = serviceUrlPrefix[ratingType];

  return fetchApi(
    `${urlPrefix}/storefront/users/${userId}/dossiers/${dossierId}/lists/${listId}/${itemType}/?${qs.stringify(
      params,
    )}`,
  ).then(response => response.json());
};

export const fetchDossierItems = async ({
  dossierId,
  listId,
  itemType,
  ratingType = ratingTypes.CREDIT_RATING,
  page,
  limit,
  start,
  sort,
  filter,
}) => {
  const dossierItemTypes = consolidatedPortfolioItemTypes[itemType];

  const fetchFunc = type =>
    fetchDossierItemsSingleType({
      dossierId,
      listId,
      itemType: type,
      ratingType,
      page,
      limit,
      start,
      sort,
      filter,
    });

  const responses = await Promise.all(dossierItemTypes.map(type => fetchFunc(type)));

  const result = {
    data: responses.map(({ data }) => data).flat(),
    total:
      responses.length > 1 ? responses.reduce((a, b) => a.total + b.total) : responses[0].total,
  };

  return result;
};

export const fetchDossiers = async ({
  ratingType = ratingTypes.CREDIT_RATING,
  requestParams: { page = 1, start = 0, limit = 25, sort },
}) => {
  const userId = getUserId();

  const params = {
    page,
    start,
    limit,
    ...(sort && { sort: sort.sortColumn }),
    ...(sort && { dir: sort.direction }),
  };

  const urlPrefix = serviceUrlPrefix[ratingType];

  try {
    return fetchApi(
      `${urlPrefix}/storefront/users/${userId}/dossiers/?${qs.stringify(params)}`,
    ).then(response => response.json());
  } catch (e) {
    throw new Error('Failed to fetch dossiers: ', e);
  }
};

export const fetchRecentDossiers = ({ page = 1, limit = 5 }) => {
  const userId = getUserId();

  const params = { page, limit };

  return fetchApi(
    `/storefront/users/${userId}/recent-dossiers?${qs.stringify(params)}`,
  ).then(response => response.json());
};

export const createDossier = async ({
  ratingType = ratingTypes.CREDIT_RATING,
  name,
  description,
  organisationId,
  id,
}) => {
  const userId = getUserId();
  const urlPrefix = serviceUrlPrefix[ratingType];
  const resourceUrl = `${urlPrefix}/storefront/users/${userId}/dossiers/${id ? `${id}/` : ''}`;

  const data = {
    name,
    description,
  };

  if (ratingType !== ratingTypes.CREDIT_RATING) {
    data.organisationId = organisationId;
  }

  const params = {
    method: id ? 'PUT' : 'POST',
    body: JSON.stringify(data),
  };
  const response = await fetchApi(resourceUrl, params);

  return id ? response : response.json();
};

export const deleteDossiers = ({ ids, ratingType = ratingTypes.CREDIT_RATING }) => {
  const userId = getUserId();

  const urlPrefix = serviceUrlPrefix[ratingType];
  const resourceUrl = `${urlPrefix}/storefront/users/${userId}/dossiers/`;

  const params = {
    method: 'DELETE',
    body: JSON.stringify(ids),
  };

  return fetchApi(resourceUrl, params);
};

export const addToDossier = async ({
  itemsData,
  dossierId,
  ratingType = ratingTypes.CREDIT_RATING,
  selectAll = false,
  sourceDossierId,
}) => {
  const userId = getUserId();

  const urlPrefix = serviceUrlPrefix[ratingType];
  const resourceUrl = `${urlPrefix}/storefront/users/${userId}/dossiers/${dossierId}/items/`;

  const { items, type } = itemsData || {};
  const data = items ? Object.keys(items).map(id => ({ artifactId: id, type })) : [];

  const params = {
    method: 'POST',
    body: JSON.stringify(!selectAll || !sourceDossierId ? data : { selectAll, sourceDossierId }),
  };

  const response = await fetchApi(resourceUrl, params);

  return response.json();
};

export const addToDossierIsin = async ({ items, dossierName }) => {
  const userId = getUserId();

  const resourceUrl = `/storefront/users/${userId}/dossiers/isin_upload?source=ISINUPLOAD`;

  const body = {
    artifacts: items,
    description: '',
    handleIssuers: true,
    name: dossierName,
  };

  const params = {
    method: 'POST',
    body: JSON.stringify(body),
  };

  const response = await fetchApi(resourceUrl, params);

  return response;
};

export const removeFromDossier = async ({
  dossierItemsIds,
  dossierId,
  ratingType = ratingTypes.CREDIT_RATING,
  selectAll = false,
}) => {
  const userId = getUserId();

  const urlPrefix = serviceUrlPrefix[ratingType];
  const resourceUrl = `${urlPrefix}/storefront/users/${userId}/dossiers/${dossierId}/items/`;

  const params = {
    method: 'DELETE',
    body: JSON.stringify(!selectAll ? dossierItemsIds : { selectAll }),
  };

  return fetchApi(resourceUrl, params);
};

export const checkDossierName = async ({ dossierName, ratingType = ratingTypes.CREDIT_RATING }) => {
  const userId = getUserId();

  const params = { dossierName };

  const urlPrefix = serviceUrlPrefix[ratingType];
  const resourceUrl = `${urlPrefix}/storefront/users/${userId}/check-dossier-name/?${qs.stringify(
    params,
  )}`;

  try {
    return await fetchApi(resourceUrl).then(response => response.json());
  } catch (e) {
    throw new Error('Failed to verify portfolio name');
  }
};

export const SIGUpdateDossier = async ({ dossierId, dossier }) => {
  const userId = getUserId();

  const params = {
    method: 'PUT',
    body: JSON.stringify({ dossierId, dossier }),
  };
  try {
    return await fetchApi(
      `/sig/api/storefront/users/${userId}/update-portfolio/`,
      params,
    ).then(response => response.json());
  } catch (e) {
    throw new Error('Failed to verify portfolio name');
  }
};

export const toggleMonitoring = async ({ itemsIds, selectAll = false, sourceDossierId }) => {
  const params = {
    method: 'POST',
    body: JSON.stringify({
      items: !selectAll ? itemsIds : undefined,
      selectAll,
      sourceDossierId,
    }),
  };
  return fetchApi(`/sig/api/update_monitoring/`, params).then(response => response.json());
};
